import { Table, Tbody, Th, Thead, Tr, Td, Stack, TableProps, Text } from '@cad-arc';
import classNames from 'classnames';
import { CSSProperties } from 'react';
import styles from './simple-table.module.css';

declare const Cell: unique symbol;

type TCell = {
    content: React.ReactNode;
    subtitle?: React.ReactNode;
    /**
     * Styles to apply per cell
     */
    cellStyle?: CSSProperties;
};

type TOpaqueCell = TCell & { _opaque: typeof Cell };

export const createCell = (params: TCell) => {
    return params as TOpaqueCell;
};

export type TSimpleTableProps = {
    rows: TOpaqueCell[][];
    /**
     * Styles to apply to each column of the table
     * (they get spread onto the `Th` and `Td` elements)
     */
    columnStyles?: CSSProperties[];
    emptyState?: React.ReactNode;
    hasBorders?: false;
    size?: TableProps['size'] | 'xs';
    /**
     * `fixed` means you need to specify the column widths
     * `auto` means the table will try and fit the columns to its content
     */
    layout?: 'fixed' | 'auto';
};

export const SimpleTable = (props: TSimpleTableProps): React.ReactElement => {
    const size = props.size || 'xs';
    const layout = props.layout || 'fixed';

    const [headerRow, ...bodyRows] = props.rows || [[]];
    const hasHeader = !!headerRow.length;

    const hasBody = !!bodyRows.length;

    /**
     * If no border, hide all the border bottoms
     *
     * Else do nothing because the table already
     * is styled with borders by default
     */
    const borderStyles: CSSProperties =
        props.hasBorders === false
            ? {
                  borderBottom: 'none',
              }
            : {};
    return (
        <Table
            size={size !== 'xs' ? size : undefined}
            className={classNames(
                {
                    [styles.fixed]: layout === 'fixed',
                    [styles.xs]: size === 'xs',
                },
                styles.table
            )}
        >
            {hasHeader && (
                <Thead>
                    <Tr>
                        {headerRow.map((headerCell, idx) => {
                            return (
                                <Th
                                    style={{
                                        ...props.columnStyles?.[idx],
                                        ...borderStyles,
                                        ...headerCell.cellStyle,
                                    }}
                                    key={idx}
                                >
                                    {headerCell.content}
                                </Th>
                            );
                        })}
                    </Tr>
                </Thead>
            )}
            {hasBody ? (
                <Tbody>
                    {bodyRows.map((bodyRow, idx) => {
                        return (
                            <Tr key={idx}>
                                {bodyRow.map((bodyCell, idx) => {
                                    return (
                                        <Td
                                            style={{
                                                ...props.columnStyles?.[idx],
                                                ...borderStyles,
                                                ...bodyCell.cellStyle,
                                                verticalAlign: 'top',
                                            }}
                                            key={idx}
                                        >
                                            {
                                                // If we have a subtitle, render it with the content
                                                // else just render the content
                                                bodyCell.subtitle ? (
                                                    <Stack spacing={0}>
                                                        <Text noOfLines={1}>
                                                            {bodyCell.content}
                                                        </Text>
                                                        <Text variant="caption" color="tertiary">
                                                            {bodyCell.subtitle}
                                                        </Text>
                                                    </Stack>
                                                ) : (
                                                    bodyCell.content
                                                )
                                            }
                                        </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                </Tbody>
            ) : (
                <Tbody>
                    <Tr>
                        <Td
                            style={{
                                textAlign: 'center' as const,
                                ...borderStyles,
                            }}
                            colSpan={headerRow.length}
                        >
                            {props.emptyState || 'No data'}
                        </Td>
                    </Tr>
                </Tbody>
            )}
        </Table>
    );
};
